import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import {
  RefreshCw,
  ArrowUp,
  ArrowDown,
  DollarSign,
  CreditCard,
  TrendingUp,
  XCircle,
  Tag,
  Percent,
  Receipt,
} from "lucide-react";
import CommonGrid from "@/components/pos/admin/common/grid";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import DateRangePresets from "@/components/pos/admin/common/date-range-presets";
import ReportExportDropdown from "@/components/pos/admin/common/report-export-dropdown";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { posApiFetch } from "@/utils/pos-api";

import { useToast } from "@/hooks/use-toast";
import { type DateRange } from "react-day-picker";
import { format } from "date-fns";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28', '#FF8042'];

const formatPercent = (value: number): string => {
  const fixed = value.toFixed(1);
  return fixed.endsWith('.0') ? Math.round(value).toString() : fixed;
};

type ReportsState = {
  activeTab?: string;
  dateRange?: { from: string; to: string };
};

const defaultReportsState: ReportsState = {
  activeTab: "summary",
};

const fetchReport = async (type: string, startDate?: string, endDate?: string) => {
  let url = `/v1/pos/reports/${type}`;
  const params = new URLSearchParams();
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  const qs = params.toString();
  if (qs) url += `?${qs}`;
  const res = await posApiFetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${type} report`);
  return res.json();
};

function StatCard({
  title,
  value,
  valueSech,
  icon: Icon,
  bgColor,
  iconBgColor,
  subtitle,
}: {
  title: string;
  value: string | number;
  valueSech?: string | number;
  icon: React.ElementType;
  bgColor: string;
  iconBgColor: string;
  subtitle?: string;
}) {
  return (
    <Card className={`border-none rounded-[4px] ${bgColor}`}>
      <CardContent className="p-[16px]">
        <div className="flex items-start w-full flex-col gap-[12px]">
          <p className="text-[var(--primary-text-color)] text-[16px] leading-[16px] font-bold">
            {title}
          </p>
          <div className="total-p flex items-center justify-between w-full">
            <p className="text-[13px] leading-[16px] text-[var(--secondary-text-color)] font-medium">
              {subtitle}
            </p>
            <p className={`text-[16px] leading-[22px] ${iconBgColor} font-medium px-[8px] justify-center rounded-[16px] text-[#ffffff] min-w-[32px] h-[32px] flex items-center`}>
              {valueSech !== undefined ? (
                <span>
                  {value}
                  <span className="opacity-70 mx-1">/</span>
                  <span className="text-yellow-200">{valueSech}</span>
                </span>
              ) : value}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ComparisonStatCard({
  title,
  currentValue,
  previousValue,
  changePercent,
  format: fmt = "number",
  bgColor,
  iconBgColor,
  currentValueSech,
  previousValueSech,
}: {
  title: string;
  currentValue: number;
  previousValue: number;
  changePercent: number;
  format?: "number" | "currency" | "currency-sech";
  bgColor: string;
  iconBgColor: string;
  currentValueSech?: number;
  previousValueSech?: number;
}) {
  const isPositive = changePercent >= 0;
  const formatValue = (val: number, valSech?: number) => {
    if (fmt === "currency-sech" && valSech !== undefined) {
      return (
        <span>
          <span>${val.toFixed(2)}</span>
          <span className="text-gray-400 mx-1">/</span>
          <span className="text-[#7F55B1]">{valSech.toFixed(4)}</span>
        </span>
      );
    }
    return fmt === "currency" ? `$${val.toFixed(2)}` : val.toLocaleString();
  };

  return (
    <Card className={`border-none rounded-[4px] ${bgColor}`}>
      <CardContent className="p-[16px]">
        <div className="flex items-start w-full flex-col gap-[8px]">
          <p className="text-[14px] leading-[16px] text-[var(--secondary-text-color)] font-medium">
            {title}
          </p>
          <div className="flex items-end justify-between w-full">
            <p className="text-[24px] leading-[28px] font-bold text-[var(--primary-text-color)]">
              {formatValue(currentValue, currentValueSech)}
            </p>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[12px] font-medium ${
              isPositive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
            }`}>
              {isPositive ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
              {formatPercent(Math.abs(changePercent))}%
            </div>
          </div>
          <p className="text-[11px] text-[var(--secondary-text-color)]">
            vs previous period: {formatValue(previousValue, previousValueSech)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function SummaryTab({ startDate, endDate }: { startDate?: string; endDate?: string }) {
  const { formatDate } = useDateFormatter();
  const { data, isLoading, error } = useQuery({
    queryKey: ["/v1/pos/reports/comparison", startDate, endDate],
    queryFn: () => fetchReport("comparison", startDate, endDate),
    retry: 2,
  });

  if (isLoading) {
    return <div className="h-[120px] flex items-center justify-center text-[var(--secondary-text-color)]">Loading...</div>;
  }

  if (error) {
    return <NoRecordFound title="Summary" description="Failed to load summary data." />;
  }

  const summary = data || {};
  const current = summary.current || {};
  const previous = summary.previous || {};
  const changes = summary.changes || {};
  const dailyBreakdown = Array.isArray(summary.dailyBreakdown) ? summary.dailyBreakdown : [];

  const dailyBreakdownColumns = [
    { label: "Day", field: "dayName" },
    {
      label: "Date",
      field: "date",
      cellTemplate: (value: string) => {
        const d = new Date(value);
        return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    },
    { label: "Orders", field: "orders" },
    {
      label: "vs Prev",
      field: "previousOrders",
      cellTemplate: (value: number, row: any) => {
        const change = (row.orders || 0) - (value || 0);
        const isPositive = change >= 0;
        return (
          <span className={`flex items-center gap-1 ${isPositive ? "text-green-600" : "text-red-600"}`}>
            {isPositive ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
            {Math.abs(change)}
          </span>
        );
      }
    },
    {
      label: "Revenue",
      field: "revenue",
      cellTemplate: (value: number) => <span>${(value || 0).toFixed(2)}</span>
    },
    {
      label: "vs Prev",
      field: "previousRevenue",
      cellTemplate: (value: number, row: any) => {
        const change = (row.revenue || 0) - (value || 0);
        const isPositive = change >= 0;
        return (
          <span className={`flex items-center gap-1 ${isPositive ? "text-green-600" : "text-red-600"}`}>
            {isPositive ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
            ${Math.abs(change).toFixed(2)}
          </span>
        );
      }
    },
    { label: "Items", field: "items" },
  ];

  return (
    <div className="space-y-6">
      {/* Key Metrics with Period Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ComparisonStatCard
          title="Net Sales (USD / SECH)"
          currentValue={current.totalRevenue || current.totalRevenueUsd || 0}
          previousValue={previous.totalRevenue || previous.totalRevenueUsd || 0}
          changePercent={changes.revenueChange || 0}
          format="currency-sech"
          currentValueSech={current.totalRevenueInSech || 0}
          previousValueSech={previous.totalRevenueInSech || 0}
          bgColor="bg-[#F2EEF7]"
          iconBgColor="bg-[#7F55B1]"
        />
        <ComparisonStatCard
          title="Total Orders"
          currentValue={current.totalOrders || 0}
          previousValue={previous.totalOrders || 0}
          changePercent={changes.ordersChange || 0}
          format="number"
          bgColor="bg-[#FEF5EF]"
          iconBgColor="bg-[#F39E60]"
        />
        <ComparisonStatCard
          title="Items Sold"
          currentValue={current.totalItems || 0}
          previousValue={previous.totalItems || 0}
          changePercent={changes.itemsChange || 0}
          format="number"
          bgColor="bg-[#F1F6F4]"
          iconBgColor="bg-[#76A68D]"
        />
        <ComparisonStatCard
          title="Avg Order (USD / SECH)"
          currentValue={current.avgOrderValue || 0}
          previousValue={previous.avgOrderValue || 0}
          changePercent={changes.avgOrderChange || 0}
          format="currency-sech"
          currentValueSech={current.avgOrderValueInSech || 0}
          previousValueSech={previous.avgOrderValueInSech || 0}
          bgColor="bg-[#E8F4FD]"
          iconBgColor="bg-[#3B82F6]"
        />
      </div>

      {/* Subtotal and Tax Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Subtotal (USD / SECH)"
          value={`$${(current.subtotal || 0).toFixed(2)}`}
          valueSech={(current.subtotalInSech || 0).toFixed(4)}
          icon={Receipt}
          bgColor="bg-[#F8F9FA]"
          iconBgColor="bg-[#6B7280]"
          subtitle="Before taxes"
        />
        <StatCard
          title="Total Tax (USD / SECH)"
          value={`$${(current.taxAmount || 0).toFixed(2)}`}
          valueSech={(current.taxAmountInSech || 0).toFixed(4)}
          icon={Receipt}
          bgColor="bg-[#FEF3C7]"
          iconBgColor="bg-[#D97706]"
          subtitle="Taxes collected"
        />
        <StatCard
          title="Cancelled Orders"
          value={current.cancelledOrders ?? 0}
          icon={XCircle}
          bgColor="bg-[#FEE2E2]"
          iconBgColor="bg-[#EF4444]"
          subtitle="Orders cancelled"
        />
        <StatCard
          title="Cancelled Value"
          value={`$${(current.cancelledValue || 0).toFixed(2)}`}
          icon={XCircle}
          bgColor="bg-[#F4F0F0]"
          iconBgColor="bg-[#8C686A]"
          subtitle="Lost revenue"
        />
      </div>

      {/* Discount Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Discounts"
          value={`$${(current.totalDiscountAmount || 0).toFixed(2)}`}
          icon={Tag}
          bgColor="bg-[#ECFDF5]"
          iconBgColor="bg-[#10B981]"
          subtitle="Amount saved by customers"
        />
        <StatCard
          title="Discounted Orders"
          value={current.discountedOrdersCount ?? 0}
          icon={Tag}
          bgColor="bg-[#F0FDF4]"
          iconBgColor="bg-[#22C55E]"
          subtitle="Orders with discount"
        />
        <StatCard
          title="Discount Rate"
          value={`${formatPercent(current.discountRate ?? 0)}%`}
          icon={Percent}
          bgColor="bg-[#EFF6FF]"
          iconBgColor="bg-[#3B82F6]"
          subtitle="% of orders discounted"
        />
        <StatCard
          title="Avg Discount"
          value={`$${(current.avgDiscountValue || 0).toFixed(2)}`}
          icon={Tag}
          bgColor="bg-[#FEF3C7]"
          iconBgColor="bg-[#F59E0B]"
          subtitle="Per discounted order"
        />
      </div>

      {/* Day-by-Day Breakdown Table */}
      {dailyBreakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-[16px]">Day-by-Day Breakdown (vs Previous Period)</CardTitle>
          </CardHeader>
          <CardContent>
            <CommonGrid
              rowData={dailyBreakdown}
              columns={dailyBreakdownColumns}
              isPaginationRequired={false}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function AnalyticsTab({ startDate, endDate }: { startDate?: string; endDate?: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["/v1/pos/reports/analytics-enhanced", startDate, endDate],
    queryFn: () => fetchReport("analytics-enhanced", startDate, endDate),
    retry: 2,
  });

  if (isLoading) {
    return <div className="h-[300px] flex items-center justify-center text-[var(--secondary-text-color)]">Loading...</div>;
  }

  if (error || !data) {
    return <NoRecordFound title="Analytics" description="Failed to load analytics data." />;
  }

  const salesByDate = Array.isArray(data?.salesByDate) ? data.salesByDate : Array.isArray(data?.chartData) ? data.chartData : Array.isArray(data) ? data : [];
  const salesByHour = Array.isArray(data?.salesByHour) ? data.salesByHour : [];
  const salesByDayOfWeek = Array.isArray(data?.salesByDayOfWeek) ? data.salesByDayOfWeek : [];
  const salesByDaypart = Array.isArray(data?.salesByDaypart) ? data.salesByDaypart : [];

  if (salesByDate.length === 0 && salesByHour.length === 0 && salesByDayOfWeek.length === 0) {
    return <NoRecordFound title="Analytics" description="No analytics data available for the selected period." />;
  }

  return (
    <div className="space-y-6">
      {/* Revenue Over Time */}
      {salesByDate.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-[16px]">Revenue Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesByDate}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(value) => {
                  const d = new Date(value);
                  return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                }} />
                <YAxis yAxisId="left" orientation="left" stroke="#7F55B1" />
                <YAxis yAxisId="right" orientation="right" stroke="#76A68D" />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    name === 'Revenue ($)' ? `$${value.toFixed(2)}` : value,
                    name
                  ]}
                />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#7F55B1" name="Revenue ($)" strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#76A68D" name="Orders" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales by Hour */}
        {salesByHour.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-[16px]">Sales by Hour</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={salesByHour}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" tickFormatter={(value) => `${value}:00`} />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number, name: string) => [name === 'revenue' ? `$${value.toFixed(2)}` : value, name === 'revenue' ? 'Revenue' : 'Orders']}
                    labelFormatter={(label) => `${label}:00 - ${Number(label) + 1}:00`}
                  />
                  <Bar dataKey="orders" fill="#F39E60" name="Orders" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Sales by Day of Week */}
        {salesByDayOfWeek.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-[16px]">Sales by Day of Week</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={salesByDayOfWeek}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tickFormatter={(value) => typeof value === 'string' ? value.substring(0, 3) : value} />
                  <YAxis />
                  <Tooltip formatter={(value: number) => [`$${value.toFixed(2)}`, 'Revenue']} />
                  <Bar dataKey="revenue" fill="#7F55B1" name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sales by Daypart */}
      {salesByDaypart.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-[16px]">Sales by Daypart</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={salesByDaypart} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(value) => `$${value}`} />
                  <YAxis dataKey="daypart" type="category" width={130} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value: number, name: string) => [
                    name === 'revenue' ? `$${value.toFixed(2)}` : value,
                    name === 'revenue' ? 'Revenue' : 'Orders'
                  ]} />
                  <Bar dataKey="revenue" fill="#76A68D" name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
              <div className="space-y-3">
                {salesByDaypart.map((dp: any, index: number) => (
                  <div key={dp.daypart} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <span className="text-[13px] font-medium">{dp.daypart}</span>
                    </div>
                    <div className="flex items-center gap-4 text-[13px]">
                      <span>{dp.orders} orders</span>
                      <span className="font-medium">${(dp.revenue || 0).toFixed(2)}</span>
                      {dp.percentOfTotal !== undefined && (
                        <span className={`px-2 py-0.5 rounded text-[11px] ${
                          dp.percentOfTotal > 30 ? 'bg-green-100 text-green-700' :
                          dp.percentOfTotal > 15 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {formatPercent(dp.percentOfTotal)}%
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function BreakdownTab({ startDate, endDate }: { startDate?: string; endDate?: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["/v1/pos/reports/breakdown", startDate, endDate],
    queryFn: () => fetchReport("breakdown", startDate, endDate),
    retry: 2,
  });

  const byCategory = useMemo(() => data?.byCategory || [], [data]);
  const byLocation = useMemo(() => data?.byLocation || [], [data]);

  if (error) {
    return <NoRecordFound title="Breakdown" description="Failed to load breakdown data." />;
  }

  if (!isLoading && byCategory.length === 0 && byLocation.length === 0) {
    return <NoRecordFound title="Breakdown" description="No breakdown data available for the selected period." />;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* By Category */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[16px]">Revenue by Category</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-[300px] flex items-center justify-center text-[var(--secondary-text-color)]">Loading...</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={byCategory}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ category, percent }: any) => `${category} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="revenue"
                    nameKey="category"
                  >
                    {byCategory.map((_: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [`$${value.toFixed(2)}`, 'Revenue']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {byCategory.map((cat: any, index: number) => (
                  <div key={cat.category} className="flex items-center justify-between text-[13px]">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <span className="capitalize">{cat.category}</span>
                    </div>
                    <div className="flex gap-4">
                      <span>{cat.itemsSold} items</span>
                      <span className="font-medium">
                        ${(cat.revenue || 0).toFixed(2)}
                        {cat.revenueInSech !== undefined && (
                          <>
                            <span className="text-gray-400 mx-1">/</span>
                            <span className="text-[#7F55B1]">{(cat.revenueInSech || 0).toFixed(4)}</span>
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* By Location */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[16px]">Revenue by Location</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-[300px] flex items-center justify-center text-[var(--secondary-text-color)]">Loading...</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={byLocation} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(value) => `$${value}`} />
                <YAxis dataKey="locationName" type="category" width={100} />
                <Tooltip formatter={(value: number) => [`$${value.toFixed(2)}`, 'Revenue']} />
                <Bar dataKey="revenue" fill="#76A68D" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TopItemsTab({ startDate, endDate }: { startDate?: string; endDate?: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["/v1/pos/reports/top-items", startDate, endDate],
    queryFn: () => fetchReport("top-items", startDate, endDate),
    retry: 2,
  });

  const topProducts = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    for (const key of ["items", "data", "topProducts"]) {
      if (Array.isArray(data[key])) return data[key];
    }
    const firstArray = Object.values(data).find((v) => Array.isArray(v));
    return Array.isArray(firstArray) ? firstArray : [];
  }, [data]);

  const topItemsColumns = useMemo(() => [
    { label: "Rank", field: "rank", cellTemplate: (_: any, row: any) => <span className="font-bold">#{(topProducts.indexOf(row) ?? -1) + 1}</span> },
    { label: "Product", field: "name", sortable: true },
    { label: "Category", field: "category" },
    { label: "Qty Sold", field: "quantitySold", sortable: true },
    {
      label: "Revenue (USD / SECH)",
      field: "revenue",
      cellTemplate: (value: number, row: any) => (
        <span className="text-[13px]">
          <span className="font-medium">${(value || 0).toFixed(2)}</span>
          <span className="text-gray-400 mx-1">/</span>
          <span className="text-[#7F55B1]">{(row.revenueInSech || 0).toFixed(4)}</span>
        </span>
      )
    },
  ], [topProducts]);

  if (error) {
    return <NoRecordFound title="Top Items" description="Failed to load top items data." />;
  }

  if (!isLoading && topProducts.length === 0) {
    return <NoRecordFound title="Top Items" description="No top items data available for the selected period." />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[16px]">Top Selling Products</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[300px] flex items-center justify-center text-[var(--secondary-text-color)]">Loading...</div>
        ) : (
          <>
            <div className="mb-6">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={topProducts.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} interval={0} fontSize={11} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="quantitySold" fill="#F39E60" name="Quantity Sold" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <CommonGrid
              rowData={topProducts}
              columns={topItemsColumns}
              isPaginationRequired={false}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PaymentsTab({ startDate, endDate }: { startDate?: string; endDate?: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["/v1/pos/reports/payments", startDate, endDate],
    queryFn: () => fetchReport("payments", startDate, endDate),
    retry: 2,
  });

  const paymentsByDay = useMemo(() => Array.isArray(data?.paymentsByDay) ? data.paymentsByDay : [], [data]);
  const exchangeRateTrend = useMemo(() => Array.isArray(data?.exchangeRateTrend) ? data.exchangeRateTrend : [], [data]);

  if (error) return <NoRecordFound title="Payments" description="Failed to load payments data." />;
  if (!isLoading && !data) return <NoRecordFound title="Payments" description="No payments data available for the selected period." />;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            title="Total Transactions"
            value={data.totalTransactions ?? 0}
            icon={CreditCard}
            bgColor="bg-[#F1F6F4]"
            iconBgColor="bg-[#76A68D]"
            subtitle="All payments"
          />
          <StatCard
            title="Total USD"
            value={`$${parseFloat(String(data.totalUsd ?? 0)).toFixed(2)}`}
            icon={DollarSign}
            bgColor="bg-[#F2EEF7]"
            iconBgColor="bg-[#7F55B1]"
            subtitle="USD collected"
          />
          <StatCard
            title="Total SECH"
            value={parseFloat(String(data.totalSech ?? 0)).toFixed(4)}
            icon={CreditCard}
            bgColor="bg-[#FEF5EF]"
            iconBgColor="bg-[#F39E60]"
            subtitle="SECH collected"
          />
          <StatCard
            title="Avg Exchange Rate"
            value={`$${parseFloat(String(data.avgExchangeRate ?? 0)).toFixed(6)}`}
            icon={TrendingUp}
            bgColor="bg-[#E8F4FD]"
            iconBgColor="bg-[#3B82F6]"
            subtitle="USD per SECH"
          />
          <StatCard
            title="With Payment Hash"
            value={data.transactionsWithHash ?? 0}
            icon={CreditCard}
            bgColor="bg-[#F4F0F0]"
            iconBgColor="bg-[#8C686A]"
            subtitle="Confirmed txns"
          />
        </div>
      )}

      {/* Payments Over Time Chart */}
      {paymentsByDay.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-[16px]">Payments Over Time (USD / SECH)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={paymentsByDay}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => {
                    const d = new Date(value);
                    return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  }}
                />
                <YAxis yAxisId="left" orientation="left" stroke="#7F55B1" />
                <YAxis yAxisId="right" orientation="right" stroke="#F39E60" />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    name === 'USD ($)' ? `$${value.toFixed(2)}` : value.toFixed(4),
                    name
                  ]}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="usd" fill="#7F55B1" name="USD ($)" />
                <Bar yAxisId="right" dataKey="sech" fill="#F39E60" name="SECH" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Transactions per Day */}
        {paymentsByDay.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-[16px]">Transactions per Day</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={paymentsByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => {
                      const d = new Date(value);
                      return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }}
                  />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number) => [value, 'Transactions']}
                  />
                  <Bar dataKey="transactions" fill="#76A68D" name="Transactions" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Exchange Rate Trend */}
        {exchangeRateTrend.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-[16px]">Exchange Rate Trend (USD per SECH)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={exchangeRateTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => {
                      const d = new Date(value);
                      return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }}
                  />
                  <YAxis domain={['auto', 'auto']} tickFormatter={(value) => `$${value.toFixed(4)}`} />
                  <Tooltip formatter={(value: number) => [`$${value.toFixed(6)}`, 'Exchange Rate']} />
                  <Line type="monotone" dataKey="rate" stroke="#3B82F6" name="USD/SECH" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function PromotionsTab({ startDate, endDate }: { startDate?: string; endDate?: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["/v1/pos/reports/promotions", startDate, endDate],
    queryFn: () => fetchReport("promotions", startDate, endDate),
    retry: 2,
  });

  const topDiscounts = useMemo(() => Array.isArray(data?.topDiscounts) ? data.topDiscounts : [], [data]);
  const discountsByDay = useMemo(() => Array.isArray(data?.discountsByDay) ? data.discountsByDay : [], [data]);

  if (error) return <NoRecordFound title="Promotions" description="Failed to load promotions data." />;
  if (!isLoading && !data) return <NoRecordFound title="Promotions" description="No promotions data available for the selected period." />;

  const discountColumns = useMemo(() => [
    { label: "Rank", field: "rank", cellTemplate: (_: any, row: any) => <span className="font-bold">#{(topDiscounts.indexOf(row) ?? -1) + 1}</span> },
    { label: "Discount Name", field: "discountName" },
    {
      label: "Promo Code",
      field: "discountCode",
      cellTemplate: (value: string | null) => value
        ? <span className="bg-gray-100 px-2 py-1 rounded text-[12px] font-mono">{value}</span>
        : <span className="text-gray-400">Auto-apply</span>
    },
    {
      label: "Type",
      field: "discountType",
      cellTemplate: (value: string) => (
        <span className={`px-2 py-1 rounded text-[11px] ${value === 'percentage' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
          {value === 'percentage' ? 'Percentage' : value ? value.charAt(0).toUpperCase() + value.slice(1) : '-'}
        </span>
      )
    },
    { label: "Uses", field: "usageCount" },
    {
      label: "Total Discount",
      field: "totalDiscountAmount",
      cellTemplate: (value: number) => <span className="font-medium text-green-600">${(value || 0).toFixed(2)}</span>
    },
    {
      label: "Avg per Order",
      field: "avgDiscountValue",
      cellTemplate: (value: number) => <span>${(value || 0).toFixed(2)}</span>
    },
  ], [topDiscounts]);

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Discounts"
            value={`$${parseFloat(String(data.totalDiscountAmount ?? 0)).toFixed(2)}`}
            icon={Tag}
            bgColor="bg-[#ECFDF5]"
            iconBgColor="bg-[#10B981]"
            subtitle="Amount saved by customers"
          />
          <StatCard
            title="Discounted Orders"
            value={data.discountedOrdersCount ?? 0}
            icon={Tag}
            bgColor="bg-[#F0FDF4]"
            iconBgColor="bg-[#22C55E]"
            subtitle="Orders with discount"
          />
          <StatCard
            title="Discount Rate"
            value={`${formatPercent(data.discountRate ?? 0)}%`}
            icon={Percent}
            bgColor="bg-[#EFF6FF]"
            iconBgColor="bg-[#3B82F6]"
            subtitle="% of orders discounted"
          />
          <StatCard
            title="Avg Discount"
            value={`$${parseFloat(String(data.avgDiscountValue ?? 0)).toFixed(2)}`}
            icon={Tag}
            bgColor="bg-[#FEF3C7]"
            iconBgColor="bg-[#F59E0B]"
            subtitle="Per discounted order"
          />
        </div>
      )}

      {/* Discounts Over Time Chart */}
      {discountsByDay.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-[16px]">Discounts Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={discountsByDay}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => {
                    const d = new Date(value);
                    return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  }}
                />
                <YAxis yAxisId="left" orientation="left" stroke="#10B981" />
                <YAxis yAxisId="right" orientation="right" stroke="#22C55E" />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    name === 'Discount Amount ($)' ? `$${value.toFixed(2)}` : value,
                    name
                  ]}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="discountAmount" fill="#10B981" name="Discount Amount ($)" />
                <Bar yAxisId="right" dataKey="discountedOrders" fill="#22C55E" name="Discounted Orders" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Top Discounts Table */}
      {topDiscounts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-[16px]">Top Applied Discounts</CardTitle>
          </CardHeader>
          <CardContent>
            <CommonGrid
              rowData={topDiscounts}
              columns={discountColumns}
              isPaginationRequired={false}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CancelledTab({ startDate, endDate }: { startDate?: string; endDate?: string }) {
  const { formatDate } = useDateFormatter();
  const { data, isLoading, error } = useQuery({
    queryKey: ["/v1/pos/reports/cancelled", startDate, endDate],
    queryFn: () => fetchReport("cancelled", startDate, endDate),
    retry: 2,
  });

  const cancelledOrders = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    for (const key of ["cancelledOrders", "cancelled", "items", "data"]) {
      if (Array.isArray(data[key])) return data[key];
    }
    const firstArray = Object.values(data).find((v) => Array.isArray(v));
    return Array.isArray(firstArray) ? firstArray : [];
  }, [data]);

  const cancelledColumns = useMemo(() => [
    { label: "Order Number", field: "orderNumber", sortable: true },
    { label: "Location", field: "locationName" },
    {
      label: "Total (USD)",
      field: "totalUsd",
      cellTemplate: (value: number) => <span className="text-[13px]">${parseFloat(String(value ?? 0)).toFixed(2)}</span>
    },
    {
      label: "Date",
      field: "cancelledAt",
      cellTemplate: (value: string) => value ? formatDate(value) : <span className="text-[var(--secondary-text-color)]">-</span>
    },
    {
      label: "Reason",
      field: "reason",
      cellTemplate: (value: string) => <span className="text-[13px] text-[var(--secondary-text-color)]">{value || "-"}</span>
    },
  ], []);

  if (error) return <NoRecordFound title="Cancelled" description="Failed to load cancelled orders data." />;

  return (
    <div className="space-y-6">
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard
            title="Total Cancelled"
            value={data.totalCancelled ?? cancelledOrders.length ?? 0}
            icon={XCircle}
            bgColor="bg-[#FEE2E2]"
            iconBgColor="bg-[#EF4444]"
            subtitle="Orders cancelled"
          />
          <StatCard
            title="Lost Revenue"
            value={`$${parseFloat(String(data.totalValue ?? 0)).toFixed(2)}`}
            icon={DollarSign}
            bgColor="bg-[#F4F0F0]"
            iconBgColor="bg-[#8C686A]"
            subtitle="Potential revenue"
          />
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-[16px]">Cancelled Orders List</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-[200px] flex items-center justify-center text-[var(--secondary-text-color)]">Loading...</div>
          ) : cancelledOrders.length > 0 ? (
            <CommonGrid
              rowData={cancelledOrders}
              columns={cancelledColumns}
              isPaginationRequired={false}
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              No cancelled orders in this period
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LocationsTab({ startDate, endDate }: { startDate?: string; endDate?: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["/v1/pos/reports/locations-enhanced", startDate, endDate],
    queryFn: () => fetchReport("locations-enhanced", startDate, endDate),
    retry: 2,
  });

  const locationData = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    for (const key of ["locations", "data", "items"]) {
      if (Array.isArray(data[key])) return data[key];
    }
    const firstArray = Object.values(data).find((v) => Array.isArray(v));
    return Array.isArray(firstArray) ? firstArray : [];
  }, [data]);

  const locationColumns = useMemo(() => [
    {
      label: "Rank",
      field: "rank",
      cellTemplate: (_: any, row: any) => <span className="font-bold">#{(locationData.indexOf(row) ?? -1) + 1}</span>
    },
    {
      label: "Location",
      field: "locationName",
      cellTemplate: (value: string, row: any) => (
        <span className="flex items-center gap-2">
          <span className="h-[18px] w-[18px] rounded-[4px]" style={{ backgroundColor: row.colorCode || "#ccc" }} />
          <span>{value || "-"}</span>
        </span>
      )
    },
    { label: "Orders", field: "totalOrders", sortable: true },
    {
      label: "Revenue (USD / SECH)",
      field: "totalRevenue",
      cellTemplate: (value: number, row: any) => (
        <span className="text-[13px]">
          <span className="font-medium">${(value || 0).toFixed(2)}</span>
          <span className="text-gray-400 mx-1">/</span>
          <span className="text-[#7F55B1]">{(row.totalRevenueInSech || 0).toFixed(4)}</span>
        </span>
      )
    },
    {
      label: "% of Total",
      field: "percentOfTotalRevenue",
      cellTemplate: (value: number) => (
        <div className="flex items-center gap-2">
          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#7F55B1] rounded-full"
              style={{ width: `${Math.min(value || 0, 100)}%` }}
            />
          </div>
          <span className="text-[12px]">{formatPercent(value || 0)}%</span>
        </div>
      )
    },
    {
      label: "Avg Order (USD / SECH)",
      field: "avgOrderValue",
      cellTemplate: (value: number, row: any) => (
        <span className="text-[13px]">
          <span>${(value || 0).toFixed(2)}</span>
          <span className="text-gray-400 mx-1">/</span>
          <span className="text-[#7F55B1]">{(row.avgOrderValueInSech || 0).toFixed(4)}</span>
        </span>
      )
    },
    { label: "Cancelled", field: "cancelledOrders" },
  ], [locationData]);

  if (error) return <NoRecordFound title="Locations" description="Failed to load location data." />;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-[16px]">Location Performance</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-[300px] flex items-center justify-center text-[var(--secondary-text-color)]">Loading...</div>
          ) : (
            <>
              <div className="mb-6">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={locationData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="locationName" />
                    <YAxis yAxisId="left" orientation="left" stroke="#7F55B1" />
                    <YAxis yAxisId="right" orientation="right" stroke="#76A68D" />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        name === 'Revenue ($)' ? `$${value.toFixed(2)}` : value,
                        name
                      ]}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="totalRevenue" fill="#7F55B1" name="Revenue ($)" />
                    <Bar yAxisId="right" dataKey="totalOrders" fill="#76A68D" name="Orders" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {locationData.length > 0 ? (
                <CommonGrid
                  rowData={locationData}
                  columns={locationColumns}
                  isPaginationRequired={false}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">No location data for this period</div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function ReportsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    document.title = "Reports | SECH Admin";
  }, []);

  const [reportsState, setReportsState] = usePagePersistentState<ReportsState>("reports", defaultReportsState);
  const activeTab = reportsState.activeTab ?? "summary";
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    if (reportsState.dateRange?.from && reportsState.dateRange?.to) {
      const from = new Date(reportsState.dateRange.from);
      const to = new Date(reportsState.dateRange.to);
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) return { from, to };
    }
    return undefined;
  });

  const startDate = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined;
  const endDate = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined;

  const dateParams = useMemo(() => {
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    return params.toString();
  }, [startDate, endDate]);

  const handleTabChange = (tab: string) => {
    setReportsState((prev) => ({ ...prev, activeTab: tab }));
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
    if (range?.from && range?.to) {
      setReportsState((prev) => ({
        ...prev,
        dateRange: { from: format(range.from!, "yyyy-MM-dd"), to: format(range.to!, "yyyy-MM-dd") },
      }));
    } else {
      setReportsState((prev) => ({ ...prev, dateRange: undefined }));
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["/v1/pos/reports"] });
    toast({ title: "Data refreshed", description: "All reports have been updated" });
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="dashboard-header min-h-[56px] px-[16px] py-[8px] flex items-center justify-between w-full border-b border-[var(--layout-border-color)]">
        <span className="text-[20px] leading-[30px] font-bold">Reports</span>
        <div className="flex items-center gap-2">
          <DateRangePresets onSelect={handleDateRangeChange} />
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            numberOfMonths={2}
            placeholder="Select date range"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="h-[36px]"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <ReportExportDropdown
            reportType={activeTab}
            dateParams={dateParams}
          />
        </div>
      </div>

      <div className="p-4">
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-8 mb-6">
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="breakdown">Breakdown</TabsTrigger>
            <TabsTrigger value="top-items">Top Items</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="promotions">Promotions</TabsTrigger>
            <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
            <TabsTrigger value="locations">Locations</TabsTrigger>
          </TabsList>

          <TabsContent value="summary">
            <SummaryTab startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsTab startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="breakdown">
            <BreakdownTab startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="top-items">
            <TopItemsTab startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="payments">
            <PaymentsTab startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="promotions">
            <PromotionsTab startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="cancelled">
            <CancelledTab startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="locations">
            <LocationsTab startDate={startDate} endDate={endDate} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
